export class ContactDetector {
  /**
   * Analyzes a text message to detect any external contact information
   * such as phone numbers, email addresses, or social media links/handles.
   */
  static detect(text: string): { hasContact: boolean; reasons: string[] } {
    if (!text) {
      return { hasContact: false, reasons: [] };
    }

    const reasons: string[] = [];
    const normalized = text.toLowerCase();

    // 1. Email pattern (standard, spaced, dotless, verbal at/arobase, or any @ symbol)
    const emailRegex = /\b[a-zA-Z0-9._%+-]{1,64}@[a-zA-Z0-9-]{1,63}\.[a-zA-Z]{2,24}\b/gi;
    const spacedEmailRegex = /\b[a-zA-Z0-9._%+-]{1,64}\s*@\s*[a-zA-Z0-9-]{1,63}\s*\.\s*[a-zA-Z]{2,24}\b/gi;
    const obfuscatedEmailRegex =
      /\b[a-zA-Z0-9._%+-]{1,64}\s*(?:@|\bat\b|\[at\]|\(at\)|_at_|\[@\]|\(@\)|\barobase?\b|\barobaz\b)\s*[a-zA-Z0-9-]{1,63}\s*(?:\.|\bdot\b|\[dot\]|\(dot\)|_dot_|\[\.\]|\(\.\)|\bpoint\b)\s*[a-zA-Z]{2,24}\b/gi;
    const hasAtSymbol = /@/.test(text);

    if (
      emailRegex.test(text) ||
      spacedEmailRegex.test(text) ||
      obfuscatedEmailRegex.test(text) ||
      hasAtSymbol
    ) {
      reasons.push('email');
    }

    // 2. Phone number pattern (including written numbers, spaces, and character substitutions)
    // Replace visual substitutions (e.g., 'o6' or 'o' followed by a digit -> '0')
    let normalizedPhoneText = normalized.replace(/o(?=\d)/g, '0');

    // Map French written numbers to digits
    const numberMap: Record<string, string> = {
      zero: '0',
      zéro: '0',
      un: '1',
      une: '1',
      deux: '2',
      trois: '3',
      quatre: '4',
      cinq: '5',
      six: '6',
      sept: '7',
      huit: '8',
      neuf: '9',
      dix: '10',
    };

    for (const [word, digit] of Object.entries(numberMap)) {
      const wordRegex = new RegExp(`\\b${word}\\b`, 'g');
      normalizedPhoneText = normalizedPhoneText.replace(wordRegex, digit);
    }

    // Clean all spacing, punctuation, and standard separators to catch space-separated numbers
    const cleanedForPhone = normalizedPhoneText.replace(/[\s._/()[\]{}\\*,;~-]/g, '');

    // Look for any sequence of 8 to 15 digits
    const phoneMatches = cleanedForPhone.match(/\d{8,15}/g);
    let hasPhone = false;
    if (phoneMatches) {
      for (const match of phoneMatches) {
        // Exclude typical dates like 17/07/2026 (17072026) or 2026-07-17 (20260717)
        const dateRegex = /\b\d{2}[/.-]\d{2}[/.-]\d{4}\b|\b\d{4}[/.-]\d{2}[/.-]\d{2}\b/g;
        const originalDates = text.match(dateRegex) || [];
        const dateDigits = originalDates.map((d) => d.replace(/\D/g, ''));

        if (!dateDigits.includes(match)) {
          hasPhone = true;
          break;
        }
      }
    }

    if (hasPhone) {
      reasons.push('phone');
    }

    // 3. Social Media & External Platforms
    // Direct matches for social domains (independent of what follows them)
    const socialDomainsRegex =
      /\b(?:instagram\.com|facebook\.com|fb\.com|linkedin\.com|twitter\.com|x\.com|tiktok\.com|snapchat\.com|t\.me|wa\.me|messenger\.com|skype\.com|discord\.gg)\b/gi;

    // Direct matches for keywords including common typos/spelling mistakes (insta, linkdin, etc.)
    const socialKeywordsRegex =
      /\b(?:ins+ta(?:gram)?|fb|face-?book|linke?d-?in|wh?ats-?app?|snap(?:chat)?|telegr?am|tik-?tok|twitt?er|discord)\b/gi;

    const handleRegex = /(?:^|\s)(@[a-zA-Z0-9_.-]{3,})\b/gi;

    if (socialDomainsRegex.test(text) || socialKeywordsRegex.test(normalized) || handleRegex.test(text)) {
      reasons.push('social_media');
    }

    // 4. General External URLs (standard and obfuscated)
    // Strip emails and @ patterns first so email domains (e.g. gmail.com in test@gmail.com) do not trigger external_link
    const textWithoutEmails = text
      .replace(emailRegex, ' ')
      .replace(spacedEmailRegex, ' ')
      .replace(obfuscatedEmailRegex, ' ')
      .replace(/[^\s@]{1,100}@[^\s@]{1,100}/g, ' ');

    const urlPrefixRegex =
      /\b(?:https?:\/\/|ftp:\/\/|www\s*(?:\.|\bdot\b|\[dot\]|\(dot\)|_dot_|\[\.\]|\(\.\))\s*)\S+/gi;
    const domainRegex =
      /\b(?:[a-zA-Z0-9-]+(?:\.|\s*(?:\bdot\b|\[dot\]|\(dot\)|_dot_|\[\.\]|\(\.\))\s*))+[a-zA-Z]{2,}(?:\/[^\s]*)?\b/gi;

    if (urlPrefixRegex.test(textWithoutEmails) || domainRegex.test(textWithoutEmails)) {
      reasons.push('external_link');
    }

    return {
      hasContact: reasons.length > 0,
      reasons,
    };
  }

  /**
   * Replaces any detected external contact details in the text with a placeholder.
   */
  static obfuscate(text: string): string {
    if (!text) {
      return text;
    }

    // Map French written numbers to digits first (to allow detection and obfuscation of verbal numbers)
    const numberMap: Record<string, string> = {
      zero: '0',
      zéro: '0',
      un: '1',
      une: '1',
      deux: '2',
      trois: '3',
      quatre: '4',
      cinq: '5',
      six: '6',
      sept: '7',
      huit: '8',
      neuf: '9',
      dix: '10',
    };

    let textWithNormalizedNumbers = text;
    for (const [word, digit] of Object.entries(numberMap)) {
      const wordRegex = new RegExp(`\\b${word}\\b`, 'gi');
      textWithNormalizedNumbers = textWithNormalizedNumbers.replace(wordRegex, digit);
    }

    let obfuscated = textWithNormalizedNumbers;

    // 1. Obfuscate emails (both standard, spaced, and obfuscated formats)
    const emailRegex = /\b[a-zA-Z0-9._%+-]{1,64}@[a-zA-Z0-9-]{1,63}\.[a-zA-Z]{2,24}\b/gi;
    const spacedEmailRegex = /\b[a-zA-Z0-9._%+-]{1,64}\s*@\s*[a-zA-Z0-9-]{1,63}\s*\.\s*[a-zA-Z]{2,24}\b/gi;
    const obfuscatedEmailRegex =
      /\b[a-zA-Z0-9._%+-]{1,64}\s*(?:@|\bat\b|\[at\]|\(at\)|_at_|\[@\]|\(@\)|\barobase?\b|\barobaz\b)\s*[a-zA-Z0-9-]{1,63}\s*(?:\.|\bdot\b|\[dot\]|\(dot\)|_dot_|\[\.\]|\(\.\)|\bpoint\b)\s*[a-zA-Z]{2,24}\b/gi;

    obfuscated = obfuscated.replace(emailRegex, '[email masqué]');
    obfuscated = obfuscated.replace(spacedEmailRegex, '[email masqué]');
    obfuscated = obfuscated.replace(obfuscatedEmailRegex, '[email masqué]');

    // 2. Obfuscate phone numbers
    // Replace standard French phone patterns
    const phoneRegex = /(?:(?:\+|00)33|0)\s*[1-9](?:[\s._/-]*\d){8}/gi;
    obfuscated = obfuscated.replace(phoneRegex, '[numéro masqué]');

    // Replace generic digit sequences of length 8-15 (excluding dates)
    const genericPhoneRegex = /(?:\+?\d[\s._/-]*){8,15}/g;
    obfuscated = obfuscated.replace(genericPhoneRegex, (match) => {
      const digitsOnly = match.replace(/\D/g, '');
      if (digitsOnly.length >= 8 && digitsOnly.length <= 15) {
        const isDate =
          /\b\d{2}[/.-]\d{2}[/.-]\d{4}\b/.test(match) || /\b\d{4}[/.-]\d{2}[/.-]\d{2}\b/.test(match);
        if (!isDate) {
          return '[numéro masqué]';
        }
      }
      return match;
    });

    // 3. Obfuscate social links / keywords / handles
    const socialDomainsRegex =
      /\b(?:instagram\.com|facebook\.com|fb\.com|linkedin\.com|twitter\.com|x\.com|tiktok\.com|snapchat\.com|t\.me|wa\.me|messenger\.com|skype\.com|discord\.gg)\b/gi;
    const socialKeywordsRegex =
      /\b(?:ins+ta(?:gram)?|fb|face-?book|linke?d-?in|wh?ats-?app?|snap(?:chat)?|telegr?am|tik-?tok|twitt?er|discord)\b/gi;
    const handleRegex = /(^|\s)(@[a-zA-Z0-9_.-]{3,})\b/gi;

    // 4. Obfuscate all general external URLs
    const urlPrefixRegex =
      /\b(?:https?:\/\/|ftp:\/\/|www\s*(?:\.|\bdot\b|\[dot\]|\(dot\)|_dot_|\[\.\]|\(\.\))\s*)\S+/gi;
    const domainRegex =
      /\b(?:[a-zA-Z0-9-]+(?:\.|\s*(?:\bdot\b|\[dot\]|\(dot\)|_dot_|\[\.\]|\(\.\))\s*))+[a-zA-Z]{2,}(?:\/[^\s]*)?\b/gi;

    obfuscated = obfuscated.replace(urlPrefixRegex, '[lien masqué]');
    obfuscated = obfuscated.replace(domainRegex, '[lien masqué]');
    obfuscated = obfuscated.replace(socialDomainsRegex, '[lien masqué]');
    obfuscated = obfuscated.replace(socialKeywordsRegex, '[réseau social masqué]');
    obfuscated = obfuscated.replace(handleRegex, '$1[pseudo masqué]');

    // 5. Catch any remaining @ patterns (e.g. user @ domain, @anything)
    const remainingAtRegex = /[^\s@]{1,100}\s*@\s*[^\s@]{1,100}/gi;
    obfuscated = obfuscated.replace(remainingAtRegex, '[contact masqué]');

    return obfuscated;
  }
}
